import {
  formatCurrency,
  formatSignedCurrency,
  formatSignedPercent,
} from "../../../constants/investmentLeague";

const hasLeagueUserIdentity = (entry) =>
  Boolean(entry?.name && (entry?.email || entry?.leaderEmail));

export const isValidLeagueUserAccess = (data) => {
  if (!Array.isArray(data) || data.length === 0) return false;
  return data.some(hasLeagueUserIdentity);
};

const parseBuUnit = (buUnit) => {
  const parts = buUnit.split(" - ");
  return {
    ticker: parts[0]?.trim() || buUnit,
    name:   parts[1]?.trim() || buUnit,
  };
};

const parseQuarterNum = (q) => {
  const match = String(q).match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
};

const sortQuarters = (quarters) =>
  [...quarters].sort((a, b) => parseQuarterNum(a) - parseQuarterNum(b));

export const mapInvestmentsResponse = (data) => {
  if (!data) return null;

  const { summary = {}, investments = [] } = data;

  const allHoldings = investments.map((inv) => {
    const { ticker, name } = parseBuUnit(inv.buUnit);
    const invested = Number(inv.investedAmount) || 0;
    const current = Number(inv.currentAmount) || 0;
    const returns = Number(inv.returns) || 0;
    const returnsPct = Number(inv.returnsPercentage) || 0;

    return {
      ticker,
      name,
      buUnit:     inv.buUnit,
      invested,
      current,
      returns,
      returnsPct,
      positive:   returnsPct >= 0,
      quarter:    inv.quarter || "Q1",
    };
  });

  const uniqueQuarters = Array.from(
    new Set(allHoldings.map((h) => h.quarter).filter(Boolean))
  );
  const quarters = sortQuarters(uniqueQuarters.length > 0 ? uniqueQuarters : ["Q1"]);
  const latestQuarter = quarters[quarters.length - 1] || "Q1";

  // Precompute totals and data by quarter
  const quarterTotalsMap = {};
  quarters.forEach((q) => {
    const qHoldings = allHoldings.filter((h) => h.quarter === q);
    const invested = qHoldings.reduce((sum, h) => sum + h.invested, 0);
    const current = qHoldings.reduce((sum, h) => sum + h.current, 0);
    const returns = qHoldings.reduce((sum, h) => sum + h.returns, 0);
    const returnsPct = invested > 0 ? (returns / invested) * 100 : 0;

    quarterTotalsMap[q] = {
      invested,
      current,
      returns,
      returnsPct,
      count: qHoldings.length,
    };
  });

  const firstQuarterInvested = quarterTotalsMap[quarters[0]]?.invested || summary.totalInvestedAmount || 0;

  const byQuarter = {};
  quarters.forEach((q) => {
    const qTotals = quarterTotalsMap[q];
    const qHoldings = allHoldings.filter((h) => h.quarter === q);

    const metrics = [
      {
        label:    "Current Value",
        value:    formatCurrency(qTotals.current),
        sub:      formatSignedCurrency(qTotals.returns),
        subColor: qTotals.returns > 0 ? "positive" : qTotals.returns < 0 ? "negative" : "neutral",
      },
      {
        label:    "Invested (Cost)",
        value:    formatCurrency(qTotals.invested),
        sub:      `Across ${qTotals.count} holdings`,
        subColor: "neutral",
      },
      {
        label:      "All-Time Return",
        value:      formatSignedPercent(summary.totalReturnsPercentage ?? 0),
        sub:        (summary.totalReturns ?? 0) >= 0 ? "Above cost basis" : "Below cost basis",
        subColor:   (summary.totalReturns ?? 0) >= 0 ? "positive" : "negative",
        valueColor: (summary.totalReturns ?? 0) >= 0 ? "positive" : "negative",
      },
      {
        label:      `${q} Return`,
        value:      formatSignedPercent(qTotals.returnsPct),
        sub:        q === latestQuarter ? "Latest quarter" : "Quarter return",
        subColor:   qTotals.returns > 0 ? "positive" : qTotals.returns < 0 ? "negative" : "neutral",
        valueColor: qTotals.returns > 0 ? "positive" : qTotals.returns < 0 ? "negative" : "neutral",
      },
      {
        label:    "Holdings",
        value:    String(qTotals.count),
        sub:      "Active positions",
        subColor: "neutral",
      },
    ];

    const idx = quarters.indexOf(q);
    const quartersUpTo = idx >= 0 ? quarters.slice(0, idx + 1) : quarters;
    const trendData = [
      { quarter: "Q0", value: firstQuarterInvested },
      ...quartersUpTo.map((quarterKey) => ({
        quarter: quarterKey,
        value: quarterTotalsMap[quarterKey]?.current || 0,
      })),
    ];

    byQuarter[q] = {
      quarter: q,
      holdings: qHoldings,
      totals: qTotals,
      metrics,
      trendData,
    };
  });

  const defaultQuarterData = byQuarter[latestQuarter] || byQuarter[quarters[0]];

  return {
    rawSummary: summary,
    allHoldings,
    quarters,
    latestQuarter,
    byQuarter,
    metrics: defaultQuarterData?.metrics || [],
    holdings: defaultQuarterData?.holdings || [],
    trendData: defaultQuarterData?.trendData || [],
    totals: defaultQuarterData?.totals || {
      invested:   summary.totalInvestedAmount,
      current:    summary.totalCurrentAmount,
      returns:    summary.totalReturns,
      returnsPct: summary.totalReturnsPercentage,
    },
  };
};

const nextQuarterLabel = (quarter) => {
  const n = parseInt(String(quarter).replace("Q", ""), 10);
  if (Number.isNaN(n)) return "Next quarter";
  return n >= 4 ? "Q1" : `Q${n + 1}`;
};

export const mapLeagueLeaderboardResponse = (data) => {
  if (!data) return null;

  const { summary, leaderboard = [] } = data;
  const eligibleUnits = leaderboard.filter((entry) => entry.eligible);
  const eligibleLabels = eligibleUnits.map((entry) => entry.code).join(" & ");
  const topPositive = summary.topUnitReturn >= 0;
  const lastRank = leaderboard.length;

  const metrics = [
    {
      label:    "Top Investor",
      value:    summary.topUnit,
      sub:      `${formatSignedPercent(summary.topUnitReturn)} all-time`,
      subColor: topPositive ? "positive" : "negative",
    },
    {
      label:    "Jackpot Eligible",
      value:    `${summary.eligibleCount} of ${summary.totalUnits}`,
      sub:      eligibleLabels || "None eligible",
      subColor: "neutral",
    },
    {
      label:    "As Of",
      value:    `${summary.quarter} Close`,
      sub:      `Next rebalance: ${nextQuarterLabel(summary.quarter)}`,
      subColor: "neutral",
    },
  ];

  const standings = leaderboard.map((entry) => {
    const hasExplicitQ1 =
      entry.q1Credit != null || entry.q1_credit != null || entry.q1Value != null;

// needs to be updated
    const q1Credit = hasExplicitQ1
      ? Number(entry.q1Credit ?? entry.q1_credit ?? entry.q1Value)
      : entry.invested > 1000000
      ? 914299
      : entry.currentValue;

    return {
      rank:          entry.rank,
      unit:          entry.name,
      code:          entry.code,
      invested:      entry.invested,
      q1Credit,
      current:       entry.currentValue,
      allTimeReturn: entry.returnPercentage,
      qtrReturn:     entry.quarterReturn,
      draw:          entry.xirr,
      highlight:     entry.rank === 1 ? "top" : entry.rank === lastRank ? "bottom" : null,
      eligible:      entry.eligible,
    };
  });

  return { metrics, standings, quarter: summary.quarter };
};

export const mapJackpotSummaryResponse = (data) => {
  if (!data) return null;

  const { summary, basePicks = [] } = data;
  const total = summary.totalInvestments || basePicks.length;
  const greenPct = total > 0 ? Math.round((summary.greenCount / total) * 100) : 0;

  return {
    summary: {
      ...summary,
      totalInvestments: total,
      greenPct,
    },
    basePicks: basePicks
      .map((pick) => ({
        label:     pick.code.split(" - ")[0]?.trim() || pick.code,
        sub:       pick.name,
        value:     formatSignedPercent(pick.returnPercentage),
        positive:  pick.isGreen,
        returnPct: pick.returnPercentage,
      }))
      .sort((a, b) => b.returnPct - a.returnPct),
  };
};

const normalizeMacCode = (code) =>
  (code ?? "").replace(/\s+/g, "").replace(/-/g, "").toUpperCase();

export const mapJackpotLeaderboardResponse = (data, userMacCode) => {
  const entries = Array.isArray(data) ? data : [];
  const userCode = normalizeMacCode(userMacCode);

  return entries.map((entry, index) => ({
    rank:     index + 1,
    unit:     entry.name || entry.code,
    code:     entry.code,
    green:    entry.greenCount,
    total:    entry.totalInvested,
    roi:      entry.returnPercentage,
    status:   entry.eligible ? "eligible" : null,
    isYou:    userCode !== "" && normalizeMacCode(entry.code) === userCode,
  }));
};

export const mapReinvestmentPreviewResponse = (data) => {
  if (!data) return null;

  const investorUnit = data.investorUnit || "MAC 2 - Hercules";
  const previousQuarter = data.previousQuarter || "Q2";
  const upcomingQuarter = data.upcomingQuarter || "Q3";
  const availableCredit = Number(data.availableCredit) || 0;
  const allocatedAmount = Number(data.allocatedAmount) || 0;
  const remainingCredit = data.remainingCredit != null
    ? Number(data.remainingCredit)
    : (availableCredit - allocatedAmount);

  // Format window dates
  const rawWindow = data.window || data.reinvestmentWindow || {};
  const startDateStr = rawWindow.start || rawWindow.startDate;
  const endDateStr = rawWindow.end || rawWindow.endDate;

  const formatDisplayDate = (val) => {
    if (!val) return "";
    if (val.includes(" ")) return val;
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return val;
      const day = String(d.getDate()).padStart(2, "0");
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${day} ${months[d.getMonth()]} ${d.getFullYear()}`;
    } catch {
      return val;
    }
  };

  const formattedStart = formatDisplayDate(startDateStr) || "01 Sep 2026";
  const formattedEnd = formatDisplayDate(endDateStr) || "31 Oct 2026";
  const displayDates = rawWindow.displayDates || `${formattedStart} – ${formattedEnd}`;
  const isOpen = rawWindow.isOpen ?? true;

  const windowInfo = {
    start: rawWindow.start || "2026-09-01",
    end: rawWindow.end || "2026-10-31",
    startDateFormatted: formattedStart,
    endDateFormatted: formattedEnd,
    startDate: formattedStart,
    endDate: formattedEnd,
    displayDates,
    isOpen,
  };

  // Jackpot status mapping
  const rawJackpot = data.jackpot || data.jackpotEligibility || {};
  let normalizedStatus = "Eligible";
  const rawStatus = (rawJackpot.status || "").toUpperCase();
  if (rawStatus === "AT_RISK" || rawStatus === "AT RISK") {
    normalizedStatus = "At Risk";
  } else if (rawStatus === "INELIGIBLE") {
    normalizedStatus = "Ineligible";
  } else if (rawStatus === "ELIGIBLE") {
    normalizedStatus = "Eligible";
  }

  const requiredPreviousUnits = rawJackpot.requiredPreviousUnits || [];
  const missingUnits = rawJackpot.missingUnits || [];

  const jackpotInfo = {
    eligible: Boolean(rawJackpot.eligible),
    status: normalizedStatus,
    requiredPreviousUnits,
    missingUnits,
    message: rawJackpot.message || "",
  };

  // Allocations mapping
  const rawAllocations = Array.isArray(data.allocations)
    ? data.allocations
    : Array.isArray(data.units)
    ? data.units
    : [];

  const allocations = rawAllocations.map((item) => {
    const buUnit = item.buUnit || item.unitName || item.code || "";
    const type = item.type || (buUnit.startsWith("MAC") ? "MAC" : "Group");
    const prevInvestment = Number(item.previousQuarterInvestment ?? item.q2Invested ?? 0);
    const prevReturn = Number(item.previousQuarterReturn ?? item.q2Return ?? 0);
    const prevRoi = item.previousQuarterRoi != null
      ? Number(item.previousQuarterRoi)
      : item.q2Roi != null
      ? Number(item.q2Roi)
      : null;
    const cumulativeVariance = item.cumulativeVariance != null
      ? Number(item.cumulativeVariance)
      : 0;
    const investedInPrev = Boolean(item.investedInPreviousQuarter ?? (prevInvestment > 0));
    const requiredForJackpot = Boolean(
      item.requiredForJackpot ??
      item.isRequiredForJackpot ??
      requiredPreviousUnits.includes(buUnit) ??
      investedInPrev
    );
    const currentInvestment = Number(item.currentQuarterInvestment ?? item.currentAllocation ?? 0);

    return {
      buUnit,
      unitName: buUnit,
      unitCode: parseBuUnit(buUnit).ticker,
      type,
      previousQuarterInvestment: prevInvestment,
      previousQuarterReturn: prevReturn,
      previousQuarterRoi: prevRoi,
      cumulativeVariance,
      investedInPreviousQuarter: investedInPrev,
      requiredForJackpot,
      currentQuarterInvestment: currentInvestment,
      status: item.status || (currentInvestment > 0 ? "INVESTED" : "NOT_INVESTED"),
    };
  });

  return {
    investorUnit,
    previousQuarter,
    upcomingQuarter,
    availableCredit,
    allocatedAmount,
    remainingCredit,
    window: windowInfo,
    reinvestmentWindow: windowInfo,
    jackpot: jackpotInfo,
    jackpotEligibility: jackpotInfo,
    allocations,
    units: allocations,
  };
};
